import type { ApiKeyAccessFormSubmitFn } from '../../form/ApiKeyAccessForm/ApiKeyAccessForm';

import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { postOauthConsent } from '../../lib/flotiq-client/auth-requests';
import { fetchOauthHelper } from '../../lib/flotiq-client/fetch-oauth-helper';

import Button from '../../components/Button/Button';

import {
  ArrowRightCurvedIcon,
  CloseIcon,
  FlotiqLogo,
} from '../../images/shapes';

import ApiKeyAccessForm from '../../form/ApiKeyAccessForm/ApiKeyAccessForm';

export type OauthConsentFormData = {
  scope: string[];
  action: string;
  space: string;
};

const OauthConsent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const spaceOptions = JSON.parse(atob(searchParams.get('spaces') || 'W10='));

  const handleConsent = async (
    action: 'approve' | 'deny',
    space: string,
    permissions: string,
  ) => {
    setLoading(true);
    searchParams.append('scope', permissions);
    const [body, _error, status] = await fetchOauthHelper<OauthConsentFormData>(
      {
        action: action,
        scope: [],
        space: space,
      },
      postOauthConsent,
      searchParams.toString(),
      t,
    );
    if (body?.url) {
      setLoading(false);
      window.location.replace(body?.url);
    } else {
      setLoading(false);
      if (status === 401) {
        toast.error(t('OauthAccess.ConsentError'));
        navigate('/oauth/consent/revoked');
      }
    }
  };

  const submitForm: ApiKeyAccessFormSubmitFn = ({ value }) => {
    handleConsent('approve', value.space, value.permissions as string);
  };

  return (
    <div
      className="bg-slate-900 dark:bg-slate-700 min-h-screen
                flex items-center justify-center p-2 md:p-6"
    >
      <div
        className="relative bg-white flex flex-col max-w-4xl items-center justify-center
                  rounded-lg shadow-2xl p-6 md:py-10 lg:px-10 dark:bg-slate-900 dark:text-white"
      >
        <FlotiqLogo className="h-10 mb-4 md:mb-6" />

        <h4 className="mb-4 md:mb-5 text-2xl xl:text-3xl">
          {t('OauthAccess.AppAccess', {
            name: searchParams.get('client_name') || '',
          })}
        </h4>
        <ApiKeyAccessForm
          spaces={spaceOptions}
          disabled={loading}
          onSubmit={submitForm}
          showPermissions
        />
        <div className="flex flex-row items-center mt-4 md:mt-10 gap-6">
          <Button
            onClick={() => handleConsent('deny', '', '')}
            buttonColor="blueBordered"
            disabled={loading}
            iconPosition="start"
            iconImage={<CloseIcon className="w-3 min-w-3" />}
          >
            {t('OauthAccess.RevokeAccess')}
          </Button>
          <Button
            type="submit"
            form="access-keys"
            name="action"
            disabled={loading}
            iconPosition="start"
            iconImage={<ArrowRightCurvedIcon className="w-3 min-w-3" />}
          >
            {t('OauthAccess.GrantAccess')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OauthConsent;
